/**
 * RecentActivityPanel — a synthesised personal activity feed. No dedicated
 * endpoint: it merges my recently-modified projects and tasks (the data already
 * in flight on the dashboard) into a single timeline, newest first. Mirrors HR's
 * client-side feed-synthesis pattern.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Card,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconActivity, IconFolder, IconChecklist } from '@tabler/icons-react';
import {
  useProjectService,
  useTaskPortalTaskService,
  type ProjectModelView,
  type TaskModelView,
} from '@actschurch/ccms-sdk';
import { PROJECTS_ROUTES, TASKS_ROUTES } from '@/config/routes';
import { parseBackendDate } from '../../utils/projectHelpers';
import { MEMBER_DASHBOARD_KEY, MY_TASKS_FILTER } from './dashboardConstants';

dayjs.extend(relativeTime);

interface ActivityItem {
  id: string;
  kind: 'project' | 'task';
  label: string;
  context: string;
  at: Date;
  to: string;
}

export function RecentActivityPanel() {
  const navigate = useNavigate();
  const projectService = useProjectService();
  const taskService = useTaskPortalTaskService();

  const { data: myProjects = [], isLoading: projectsLoading } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-projects'],
    queryFn: () => projectService.getMyListView(),
  });
  const { data: myTasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-tasks'],
    queryFn: () => taskService.getMyTasks(MY_TASKS_FILTER),
  });

  const isLoading = projectsLoading || tasksLoading;

  const items = useMemo<ActivityItem[]>(() => {
    const out: ActivityItem[] = [];

    (myProjects as ProjectModelView[])
      .filter((p) => !p.isArchived)
      .forEach((p) => {
        const at = parseBackendDate(p.modifiedDateTime ?? p.createdDateTime);
        if (!at) return;
        out.push({
          id: `project-${p.projectID}`,
          kind: 'project',
          label: p.title || `Project ${p.projectID}`,
          context: 'Project updated',
          at,
          to: PROJECTS_ROUTES.VIEW(p.projectID),
        });
      });

    (myTasks as TaskModelView[]).forEach((t) => {
      const at = parseBackendDate(t.modifiedDateTime ?? t.createdDateTime);
      if (!at) return;
      out.push({
        id: `task-${t.taskID}`,
        kind: 'task',
        label: t.title || `Task ${t.taskID}`,
        context: t.projectName ? `Task · ${t.projectName}` : 'Task updated',
        at,
        to: TASKS_ROUTES.DETAIL(String(t.taskID)),
      });
    });

    return out.sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, 6);
  }, [myProjects, myTasks]);

  return (
    <Card withBorder padding="lg" radius="md">
      <Group gap="xs" mb="sm">
        <ThemeIcon size="md" variant="light" color="cyan" radius="xl">
          <IconActivity size={16} />
        </ThemeIcon>
        <Title order={5}>Recent Activity</Title>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={32} radius="sm" />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No recent activity yet.
        </Text>
      ) : (
        <Stack gap="sm">
          {items.map((item) => (
            <Group
              key={item.id}
              gap="sm"
              wrap="nowrap"
              align="flex-start"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(item.to)}
            >
              <ThemeIcon
                size="md"
                variant="light"
                radius="xl"
                color={item.kind === 'project' ? 'indigo' : 'blue'}
              >
                {item.kind === 'project' ? (
                  <IconFolder size={14} />
                ) : (
                  <IconChecklist size={14} />
                )}
              </ThemeIcon>
              <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={500} truncate>
                  {item.label}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {item.context} · {dayjs(item.at).fromNow()}
                </Text>
              </Stack>
            </Group>
          ))}
        </Stack>
      )}
    </Card>
  );
}
